import React, { useState, useEffect } from "react";
import { TerminalWindow, ArrowClockwise, Check, Copy, WifiHigh } from "@phosphor-icons/react";
import { motion } from "motion/react";
import { engineerApiResponse } from "../../data/content";

interface EngineerData {
  engineer: string;
  role: string;
  location: string;
  availability: string;
  skills: string[];
  direct_inbox: string;
}

export const EdgeTelemetryConsole: React.FC = () => {
  const [data, setData] = useState<EngineerData>(engineerApiResponse);
  const [isPinging, setIsPinging] = useState(false);
  const [lastPing, setLastPing] = useState<number | null>(null);
  const [copiedCurl, setCopiedCurl] = useState(false);
  const [originUrl, setOriginUrl] = useState("https://creasy.ir");

  // Determine dynamic website domain/origin for the cURL command
  useEffect(() => {
    if (typeof window !== "undefined" && window.location.origin) {
      setOriginUrl(window.location.origin);
    }
  }, []);

  // Fetch real endpoint data on mount and measure initial ping
  const executeRealRequest = async () => {
    setIsPinging(true);
    const startTime = performance.now();

    try {
      const res = await fetch("/v1/engineer");
      if (res.ok) {
        const json = await res.json();
        setData(json);
      }
    } catch {
      // Graceful fallback to imported config if network fails
      setData(engineerApiResponse);
    } finally {
      const duration = Math.max(8, Math.round(performance.now() - startTime));
      setLastPing(duration);
      setIsPinging(false);
    }
  };

  useEffect(() => {
    executeRealRequest();
  }, []);

  const curlCommand = `curl -s ${originUrl}/v1/engineer | jq .`;

  const handleCopyCurl = () => {
    navigator.clipboard.writeText(curlCommand);
    setCopiedCurl(true);
    setTimeout(() => setCopiedCurl(false), 2000);
  };

  return (
    <div className="rounded-2xl bg-[#181716] border border-[#2A2926] overflow-hidden shadow-2xl flex flex-col justify-between h-full">
      {/* Console Top Window Bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-[#1F1E1B] border-b border-[#282724]">
        <div className="flex items-center gap-2">
          {/* Mac/Unix Window Dots */}
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-[#E5983A]/40 border border-[#E5983A]/70" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#3A3935] border border-[#484641]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#3A3935] border border-[#484641]" />
          </div>
          <span className="text-xs font-mono text-[#8C8A84] ml-2 flex items-center gap-1.5">
            <TerminalWindow size={13} className="text-[#E5983A]" />
            <span>tehran-edge-01.creasy.ir</span>
          </span>
        </div>

        {/* Live Status Indicator */}
        <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#141413] border border-[#2B2A27] text-[11px] font-mono">
          <motion.span
            animate={{ opacity: [1, 0.4, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="w-1.5 h-1.5 rounded-full bg-[#4ADE80]"
          />
          <span className="text-[#A6A39A]">200 OK · HTTP/2</span>
        </div>
      </div>

      {/* Terminal Code Body */}
      <div className="p-5 sm:p-6 font-mono text-xs sm:text-[13px] leading-relaxed select-text space-y-3">
        {/* Dynamic Command Line Input with Live URL */}
        <div className="flex items-center gap-2 text-[#8C8A84] border-b border-[#242320] pb-2.5 overflow-x-auto">
          <span className="text-[#E5983A] font-bold shrink-0">$</span>
          <span className="text-[#DCD8CE] whitespace-nowrap">{curlCommand}</span>
        </div>

        {/* Real Dynamic JSON Spec Display */}
        <div className="text-[#A6A39A] space-y-0.5 pt-1 overflow-x-auto">
          <div><span className="text-[#75736D]">{"{"}</span></div>
          <div className="pl-4">
            <span className="text-[#E5983A]">"engineer"</span>: <span className="text-[#F3F2EE]">"{data.engineer}"</span>,
          </div>
          <div className="pl-4">
            <span className="text-[#E5983A]">"role"</span>: <span className="text-[#F3F2EE]">"{data.role}"</span>,
          </div>
          <div className="pl-4">
            <span className="text-[#E5983A]">"location"</span>: <span className="text-[#F3F2EE]">"{data.location}"</span>,
          </div>
          <div className="pl-4">
            <span className="text-[#E5983A]">"availability"</span>: <span className="text-[#4ADE80]">"{data.availability}"</span>,
          </div>
          <div className="pl-4">
            <span className="text-[#E5983A]">"skills"</span>: <span className="text-[#75736D]">[</span>
            {data.skills?.map((skill, idx) => (
              <span key={skill}>
                <span className="text-[#C8C6BE]">"{skill}"</span>
                {idx < data.skills.length - 1 ? <span className="text-[#75736D]">, </span> : null}
              </span>
            ))}
            <span className="text-[#75736D]">]</span>,
          </div>
          <div className="pl-4">
            <span className="text-[#E5983A]">"direct_inbox"</span>: <span className="text-[#E5983A]">"{data.direct_inbox}"</span>
          </div>
          <div><span className="text-[#75736D]">{"}"}</span></div>
        </div>

        {/* Live SVG Signal Waveform with Dynamic Latency */}
        <div className="pt-3 border-t border-[#242320]">
          <div className="flex items-center justify-between text-[11px] text-[#75736D] mb-1.5">
            <span className="flex items-center gap-1.5">
              <WifiHigh size={13} className="text-[#E5983A]" />
              <span>LIVE EDGE LINK</span>
            </span>
            <span className="font-mono text-[#E5983A]">
              {isPinging ? "Querying /v1/engineer..." : lastPing !== null ? `${lastPing}ms latency` : "Ready"}
            </span>
          </div>
          <div className="h-8 w-full bg-[#121110] rounded-lg border border-[#242320] flex items-center px-2 overflow-hidden">
            <svg className="w-full h-5 text-[#E5983A]/60" viewBox="0 0 280 20" fill="none" preserveAspectRatio="none">
              <path
                d="M0 10 Q 20 10, 35 4 T 70 10 T 105 16 T 140 10 T 175 3 T 210 10 T 245 15 T 280 10"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M0 10 Q 20 10, 35 4 T 70 10 T 105 16 T 140 10 T 175 3 T 210 10 T 245 15 T 280 10 L 280 20 L 0 20 Z"
                fill="currentColor"
                fillOpacity="0.08"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Interactive Action Footer */}
      <div className="px-4 py-3 bg-[#141413] border-t border-[#242320] flex flex-wrap items-center justify-between gap-2.5">
        <button
          id="console-ping-btn"
          onClick={executeRealRequest}
          disabled={isPinging}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#201F1D] hover:bg-[#282724] border border-[#2E2C28] text-xs font-mono text-[#DCD8CE] hover:text-[#E5983A] transition cursor-pointer disabled:opacity-50"
        >
          <ArrowClockwise
            size={13}
            weight="bold"
            className={`${isPinging ? "animate-spin text-[#E5983A]" : ""}`}
          />
          <span>{isPinging ? "Querying..." : "Execute Query"}</span>
        </button>

        <button
          id="console-copy-curl-btn"
          onClick={handleCopyCurl}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#201F1D] hover:bg-[#282724] border border-[#2E2C28] text-xs font-mono text-[#DCD8CE] hover:text-[#E5983A] transition cursor-pointer"
        >
          {copiedCurl ? (
            <>
              <Check size={13} weight="bold" className="text-[#4ADE80]" />
              <span className="text-[#4ADE80]">cURL Copied</span>
            </>
          ) : (
            <>
              <Copy size={13} />
              <span>Copy cURL</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};
